import React from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  useTheme,
} from '@mui/material';
import { CustomControlImplementation } from '../../../../common/CustomControl';
import { FormField } from '../../../FormField';
import { generateDataTestId } from '../../../../../utils';
import { useDynamicLookup } from '../hooks';
import { LookupOption } from '../types';

const DynamicLookup: React.FC<CustomControlImplementation> = ({
  field,
  fieldValue,
  onFieldValueChange,
  fieldMeta,
  fieldPresentation,
  handleFieldTouch,
  fieldFrame,
  resourceFormContext,
}) => {
  const dynamicLookup = fieldMeta?.dynamicLookup;

  const { options, isLoading, parentValue, parentFieldMetadata, lookupParent } =
    useDynamicLookup({
      dynamicLookup,
      onFieldValueChange,
      resourceFormContext,
    });

  const { isError, isDisabled, isReadOnly } = fieldFrame;
  const theme = useTheme();

  // Use readonly color (#E4E4E4) when disabled to match POSTAL_CODE field behavior
  const backgroundColor = isReadOnly
    ? theme.palette.states.readonly
    : isDisabled || !parentValue
      ? theme.palette.states.readonly
      : theme.palette.states.default;

  // Early return after all hooks have been called
  if (!dynamicLookup) {
    console.warn(
      `DynamicLookup: No dynamicLookup configuration found in fieldMeta for: ${field.Key}`,
    );
    return null;
  }

  // Find current value in options
  const currentOption = fieldValue
    ? options.find((opt) => opt.value === fieldValue) || {
        value: fieldValue,
        label: fieldValue,
      }
    : null;

  const handleChange = (option: LookupOption | null) => {
    const newValue = option?.value || '';
    if (newValue !== fieldValue) {
      onFieldValueChange(newValue);
    }
  };

  const Search = (
    <Autocomplete
      data-testid={generateDataTestId(field, fieldMeta, 'searchField')}
      value={currentOption}
      onChange={(_, newValue) => handleChange(newValue)}
      onClose={handleFieldTouch}
      options={options}
      getOptionLabel={(option) => option.label}
      loading={isLoading}
      disableClearable={false}
      disabled={isDisabled || !parentValue}
      readOnly={isReadOnly}
      renderInput={(params) => (
        <TextField
          {...params}
          error={isError}
          onBlur={handleFieldTouch}
          placeholder={
            !parentValue
              ? `Select ${parentFieldMetadata?.Name || lookupParent} first`
              : `Search ${field.Label || fieldMeta?.Name}`
          }
          sx={{
            '& .MuiOutlinedInput-root.Mui-disabled.Mui-focused, & .MuiOutlinedInput-root.Mui-readOnly.Mui-focused':
              {
                '& > fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.23) !important',
                  borderWidth: '1px !important',
                },
              },
            '& .MuiOutlinedInput-root': {
              backgroundColor: `${backgroundColor} !important`,
            },
          }}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          {option.label}
        </Box>
      )}
      isOptionEqualToValue={(option, value) => option.value === value.value}
    />
  );

  return (
    <FormField
      field={field}
      fieldMeta={fieldMeta}
      fieldPresentation={fieldPresentation}
      fieldValue={fieldValue}
      onFieldValueChange={onFieldValueChange}
      {...fieldFrame}
      showRemaining={false}
    >
      {Search}
    </FormField>
  );
};

export default React.memo(DynamicLookup);
